import { Fragment, type ReactNode } from "react";
import {
  Box,
  Button,
  ExpandableSearch,
  Icon,
  Separator,
  Text,
  type Omittable,
} from "@salestrak/ui";
import {
  OUTCOME_TONE_CONFIG,
  OUTCOME_TONES,
  type OutcomeTone,
} from "#app/lib/outcome-tone";
import { SettingsGroupEmptyRow, SettingsGroupHeader } from "./settings-group";

type OutcomeToneSectionsProps<T> = Omittable<{
  items: T[];
  toneOf: (item: T) => OutcomeTone;
  keyOf: (item: T, index: number) => string;
  renderRow: (item: T) => ReactNode;
  /** Create into the section whose `+` was pressed — the tone is the seed, so
   *  the dialog opens already answering "which kind of outcome is this".
   *  Absent on a read-only render: the sections list with no `+`. */
  onAdd?: (tone: OutcomeTone) => void;
  /** The word the search matches an item on. */
  labelOf: (item: T) => string;
  /** The applied search. ONE field for the whole vocabulary, in the first
   *  section's header left of its `+`: it narrows every section at once, and
   *  every section stays put with its own "No results" row, so the tone
   *  structure never disappears under a query. Hidden while the vocabulary
   *  is empty — nothing to search. */
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
}>;

/**
 * An outcome vocabulary laid out one section per tone — Positive, Neutral,
 * Negative — each with its own `+`. Every tone gets a section even with nothing
 * in it: the empty row is what tells an admin the tone exists and can be filled.
 *
 * Deliberately NOT drag-sortable. Tone feeds reporting, so an outcome's section
 * is a property of the outcome, not an arrangement someone chose; a grip here
 * would promise a hand-ordered vocabulary that the posting pickers (one flat
 * list in pool order) do not honor.
 *
 * Renders as ONE child of `SettingsGroup` and places ALL of its own separators:
 * full-bleed between sections and under each tone header, inset between the
 * rows of a section. The group's auto-separator only fires BETWEEN children, so
 * splitting this into a child per section would hand it dividers it cannot
 * place correctly.
 */
export function OutcomeToneSections<T>({
  items,
  toneOf,
  keyOf,
  renderRow,
  onAdd,
  labelOf,
  searchQuery,
  onSearchQueryChange,
}: OutcomeToneSectionsProps<T>) {
  const query = searchQuery.trim().toLowerCase();
  const matches = (item: T): boolean =>
    query === "" || labelOf(item).toLowerCase().includes(query);
  return (
    <>
      {OUTCOME_TONES.map((tone, sectionIndex) => {
        const label = OUTCOME_TONE_CONFIG[tone].label;
        const sectionItems = items.filter(
          (item) => toneOf(item) === tone && matches(item),
        );
        return (
          <Fragment key={tone}>
            {sectionIndex > 0 ? <Separator /> : null}
            <SettingsGroupHeader>
              <Text size="xs" weight="medium" variant="muted">
                {label}
              </Text>
              <Box direction="row" align="center" gap="xs">
                {sectionIndex === 0 && items.length > 0 ? (
                  <ExpandableSearch
                    value={searchQuery}
                    onValueChange={onSearchQueryChange}
                    size="xs"
                    tooltip="Search outcomes"
                  />
                ) : null}
                {onAdd ? (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Add ${label.toLowerCase()} outcome`}
                    onClick={() => onAdd(tone)}
                  >
                    <Icon name="plus" size="md" />
                  </Button>
                ) : null}
              </Box>
            </SettingsGroupHeader>
            <Separator />
            {sectionItems.length === 0 ? (
              <SettingsGroupEmptyRow
                label={query ? "No results" : `No ${label.toLowerCase()} outcomes`}
              />
            ) : (
              sectionItems.map((item, index) => (
                <Fragment key={keyOf(item, index)}>
                  {index > 0 ? (
                    <Box paddingX="md">
                      <Separator />
                    </Box>
                  ) : null}
                  {renderRow(item)}
                </Fragment>
              ))
            )}
          </Fragment>
        );
      })}
    </>
  );
}
